
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Globe } from 'lucide-react';
import { COUNTRIES } from '@/lib/constants';

interface CountryFilterProps {
  value: string;
  onChange: (value: string) => void;
}

export const CountryFilter = ({ value, onChange }: CountryFilterProps) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Filter countries based on input
  const filteredCountries = useMemo(() => {
    return COUNTRIES.filter(country => 
      country.name.toLowerCase().includes(value.toLowerCase())
    ).slice(0, 10); // Limit to 10 results for better performance
  }, [value]);
  
  // Handle selecting a country from dropdown
  const handleCountrySelect = (country: string) => {
    onChange(country);
    setFocused(false);
  };

  // Handle click on the input
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFocused(true);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div>
      <Label htmlFor="country-filter" className="text-sm font-medium mb-2 block">
        Filter by Country
      </Label>
      <div className="relative" ref={inputRef}>
        <Globe className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
        <Input
          id="country-filter"
          placeholder="Select country"
          className="pl-9"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onClick={handleClick}
        />
        
        {/* Country Search Dropdown */}
        {focused && filteredCountries.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
            <ul className="py-1">
              {filteredCountries.map((country) => (
                <li 
                  key={country.code}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  onClick={() => handleCountrySelect(country.name)}
                >
                  {country.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
